// Dependencies
import React, { useState, useEffect, useCallback } from "react";
import { Avatar as AvatarIcon } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Loading } from "@gpstrackit/gtc-react-controls";

// Actions
import { getSecureFile, clearRedux } from "Redux/actions";

import "./index.scss"

const Avatar = ({ value, width = 40, name, color, isOnline, onClick, style, fileKey = 'defaultKey', showStatusIcon = false }) => {
  const dispatch = useDispatch();
  
  // Redux state
  const secureUrl = useSelector((state) => state.commomRedux?.[fileKey]);
  const isLoadingSecureFile = useSelector((state) => state.commomRedux?.[`loading-${fileKey}`]);
  
  // Local state for image loading and error handling
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Fetch secure file if fileKey is provided and we don't have the URL yet
  useEffect(() => {
    if (fileKey && fileKey !== 'defaultKey' && !secureUrl && !isLoadingSecureFile) {
      dispatch(getSecureFile({ fileKey, timeout: 600 }));
    }
  }, [fileKey, secureUrl, isLoadingSecureFile, dispatch]);

  // Reset error state when we have new sources available
  useEffect(() => {
    if (secureUrl || value?.url || value?.file) {
      setImageError(false);
    }
  }, [secureUrl, value?.url, value?.file]);

  // Reset retry count when fileKey changes (new avatar instance)
  useEffect(() => {
    setRetryCount(0);
  }, [fileKey]);

  // Determine the image source with priority order
  const getImageSource = useCallback(() => {
    // Priority 1: Secure URL from Redux (if available and valid)
    if (secureUrl && !imageError) {
      return secureUrl;
    }
    
    // Priority 2: Direct URL from value prop
    if (value?.url && !imageError) {
      return value.url;
    }
    
    // Priority 3: File object (create object URL)
    if (value?.file && !imageError) {
      return URL.createObjectURL(value.file);
    }
    
    // Fallback: Default blank avatar
    return "/assets/icons/chat/chat-avatar.png";
  }, [secureUrl, value?.url, value?.file, imageError]);

  const imageSrc = getImageSource();

  // Handle image load error
  const handleImageError = useCallback(() => {
    setImageError(true);
    
    if (secureUrl && retryCount === 0 && fileKey && fileKey !== 'defaultKey') {
      setRetryCount(1);
      dispatch(clearRedux("COMMON", { [fileKey]: null, [`loading-${fileKey}`]: false }));
    }
  }, [secureUrl, retryCount, fileKey, dispatch]);

  // Handle image load start
  const handleImageLoadStart = useCallback(() => {
    setImageError(false);
  }, []);

  // Handle image load success
  const handleImageLoad = useCallback(() => {
    setImageError(false);
    setRetryCount(0); 
  }, []);

  // Common avatar styles
  const avatarStyles = {
    width: width,
    height: width,
    margin: '0 auto',
    cursor: onClick ? 'pointer' : 'default',
    ...style
  };

  // Show loading spinner while fetching secure file
  if (isLoadingSecureFile && !secureUrl) {
    return (
      <div style={{ ...avatarStyles, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading size={width * 0.6} />
      </div>
    );
  }

  // Show initials if no valid image source and name is provided
  const shouldShowInitials = (!secureUrl && !value?.url && !value?.file) || imageError;
  
  if (shouldShowInitials && name) {
    return (<div style={{position:'relative'}}>
      <AvatarIcon
        src={'/assets/icons/chat/chat-avatar.png'}
        className={color}
        sx={avatarStyles}
        onClick={onClick}
        aria-label={`Avatar for ${name}`}
      >
        {/* {name.charAt(0).toUpperCase()} */}
      </AvatarIcon>
      {showStatusIcon && <div className={`icon-status ${isOnline ? 'onl' : 'off'}`} ></div>}
    </div>);
  }

  // Show avatar with image
  return (<div style={{position:'relative'}}>
    <AvatarIcon
      src={imageSrc}
      className={color}
      sx={avatarStyles}
      onClick={onClick}
      onError={handleImageError}
      onLoadStart={handleImageLoadStart}
      onLoad={handleImageLoad}
      aria-label={name ? `Avatar for ${name}` : "Avatar"}
    />
    {showStatusIcon && <div className={`icon-status ${isOnline ? 'onl' : 'off'}`} ></div>}
  </div>);
};

export default Avatar;